import React, { useEffect, useState } from 'react';

import { BlockStack, Box, Card, Checkbox, InlineStack, Page, Select, Text, TextField } from '@shopify/polaris';
import useAdminApi from '../hooks/Api';
import usePartialState from '../hooks/PartialState';
import { useNavigate, useParams } from 'react-router-dom';
import CountryPicker from '../components/CountryPicker';
import PlanetFiscalNumberType from '../enums/PlanetFiscalNumberType';
import PlanetInvoiceNumberType from '../enums/PlanetInvoiceNumberType';

function LocationDetailsPage() {
    const navigate = useNavigate();
    const { request } = useAdminApi();

    const [location, setLocation] = useState(null);
    const [countries, setCountries] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const [form, setFormState] = usePartialState({
        processing: false,
        planetUsername: '',
        planetPassword: '',
        planetMerchantId: '',
        planetMerchantVatId: '',
        planetMerchantIsoCountry: '',
        printNodeApiKey: '',
        disableCountrySelector: false,
        is_production: false,
        printNodeEnabled: false,
        allowConsolidation: true,
        autoVoidingEnabled: false,
        showAdvancedSettings: false,
        fiscalFieldMappings: {
            invoice_number: { namespace: '', key: '', metaobject_property: '', filter: 'last' },
            fiscal_number: { namespace: '', key: '', metaobject_property: '', filter: 'last' },
            hash: { namespace: '', key: '', metaobject_property: '', filter: 'last' },
            void_fiscal_number: { namespace: '', key: '', metaobject_property: '', filter: 'last' },
        },
        // Line splitting for customs compliance (PT, ES, FR)
        lineSplittingEnabled: false,
        lineSplittingThreshold: '500.00',
        // Serial number mapping - up to 4 product attributes
        serialNumberMappings: ['', '', '', ''],
        error: null,
    });

    // Available product attributes for serial number mapping
    const serialNumberAttributeOptions = [
        { label: 'Select attribute...', value: '' },
        { label: 'SKU', value: 'sku' },
        { label: 'Variant Title (Size/Color)', value: 'variant_title' },
        { label: 'Vendor/Brand', value: 'vendor' },
        { label: 'Product Type', value: 'product_type' },
        { label: 'Product Title', value: 'title' },
        { label: 'Barcode', value: 'barcode' },
    ];

    const { id: locationId } = useParams();

    async function loadData() {
        setIsLoading(true);

        const { data: location } = await request(`locations/${locationId}`);
        setLocation(location);

        if (!location) {
            return;
        }

        const { data: countries } = await request('countries');
        setCountries(countries);

        setFormState({
            processing: false,
            planetUsername: '',
            planetPassword: '',
            planetMerchantId: location.planet_merchant_id,
            planetMerchantVatId: '',
            planetMerchantIsoCountry: `${location.planet_merchant_iso_country}`,
            planetFiscalNumberType: location.planet_fiscal_number_type,
            planetInvoiceNumberType: location.planet_invoice_number_type,
            disableCountrySelector: location.planet_merchant_iso_country !== null,
            isProduction: location.is_production,
            printNodeEnabled: location.print_node_enabled,
            printNodeApiKey: location.print_node_api_key,
            allowConsolidation: location.allow_consolidation ?? true,
            autoVoidingEnabled: location.auto_voiding_enabled ?? false,
            showAdvancedSettings: location.show_advanced_settings ?? false,
            fiscalFieldMappings: location.fiscal_field_mappings || {
                invoice_number: { namespace: '', key: '', metaobject_property: '', filter: 'last' },
                fiscal_number: { namespace: '', key: '', metaobject_property: '', filter: 'last' },
                hash: { namespace: '', key: '', metaobject_property: '', filter: 'last' },
                void_fiscal_number: { namespace: '', key: '', metaobject_property: '', filter: 'last' },
            },
            // Line splitting configuration
            lineSplittingEnabled: location.line_splitting_enabled ?? false,
            lineSplittingThreshold: location.line_splitting_threshold ?? '500.00',
            // Serial number mappings - ensure array of 4 elements
            serialNumberMappings: [...(location.serial_number_mappings || []), '', '', '', ''].slice(0, 4),
            error: null,
        });

        setIsLoading(false);
    }

    async function handleSaveChanges(form) {
        setFormState({ processing: true, error: null });

        const { data, ok } = await request.patch(`locations/${locationId}`, {
            planet_merchant_username: form.planetUsername,
            planet_merchant_password: form.planetPassword,
            planet_merchant_id: form.planetMerchantId,
            planet_merchant_vat_id: form.planetMerchantVatId,
            planet_fiscal_number_type: form.planetFiscalNumberType,
            planet_invoice_number_type: form.planetInvoiceNumberType,
            is_production: form.isProduction,
            print_node_enabled: form.printNodeEnabled,
            print_node_api_key: form.printNodeApiKey,
            allow_consolidation: form.allowConsolidation,
            auto_voiding_enabled: form.autoVoidingEnabled,
            show_advanced_settings: form.showAdvancedSettings,
            fiscal_field_mappings: form.fiscalFieldMappings,
            // Line splitting configuration
            line_splitting_enabled: form.lineSplittingEnabled,
            line_splitting_threshold: parseFloat(form.lineSplittingThreshold) || 500.0,
            // Serial number mappings - filter out empty values
            serial_number_mappings: form.serialNumberMappings.filter((v) => v),
        });

        if (!ok) {
            setFormState({ error: data ? data.message : 'Could not save the location details at this time!' });
        }

        setFormState({ processing: false });

        if (ok) {
            loadData();
        }
    }

    useEffect(() => {
        loadData();
    }, []);

    // Helper function to update fiscal field mappings
    const updateFiscalFieldMapping = (field, key, value) => {
        setFormState({
            fiscalFieldMappings: {
                ...form.fiscalFieldMappings,
                [field]: {
                    ...form.fiscalFieldMappings[field],
                    [key]: value,
                },
            },
        });
    };

    // Helper function to render fiscal field content
    const renderFiscalFieldContent = (fieldName) => (
        <BlockStack gap="300">
            <InlineStack gap="300" flexChildre={1}>
                <TextField
                    label="Namespace"
                    value={form.fiscalFieldMappings[fieldName]?.namespace || ''}
                    onChange={(value) => updateFiscalFieldMapping(fieldName, 'namespace', value)}
                    placeholder="e.g., fiscal_module"
                    readOnly={form.processing}
                />
                <TextField
                    label="Key"
                    value={form.fiscalFieldMappings[fieldName]?.key || ''}
                    onChange={(value) => updateFiscalFieldMapping(fieldName, 'key', value)}
                    placeholder="e.g., receipt_number"
                    readOnly={form.processing}
                />
                <TextField
                    label="Metaobject Property (optional)"
                    value={form.fiscalFieldMappings[fieldName]?.metaobject_property || ''}
                    onChange={(value) => updateFiscalFieldMapping(fieldName, 'metaobject_property', value)}
                    placeholder="e.g., invoice_number (for metaobject fields only)"
                    readOnly={form.processing}
                />
                <Select
                    label="Filter"
                    value={form.fiscalFieldMappings[fieldName]?.filter || ''}
                    onChange={(value) => updateFiscalFieldMapping(fieldName, 'filter', value)}
                    options={[
                        { label: 'Last (most recent)', value: 'last' },
                        { label: 'First (oldest)', value: 'first' },
                        { label: 'Empty (no filter)', value: '' },
                    ]}
                    disabled={form.processing}
                />
            </InlineStack>
        </BlockStack>
    );

    const hasChanges =
        !isLoading &&
        !form.processing &&
        (form.planetMerchantId !== location?.planet_merchant_id ||
            form.planetMerchantVatId.length > 0 ||
            form.planetUsername.length > 0 ||
            form.planetPassword.length > 0 ||
            form.planetFiscalNumberType !== location.planet_fiscal_number_type ||
            form.planetInvoiceNumberType !== location.planet_invoice_number_type ||
            form.isProduction !== location.is_production ||
            form.printNodeEnabled !== location.print_node_enabled ||
            form.printNodeApiKey !== location.print_node_api_key ||
            form.allowConsolidation !== (location.allow_consolidation ?? true) ||
            form.autoVoidingEnabled !== (location.auto_voiding_enabled ?? false) ||
            form.showAdvancedSettings !== (location.show_advanced_settings ?? false) ||
            JSON.stringify(form.fiscalFieldMappings) !== JSON.stringify(location?.fiscal_field_mappings || {}) ||
            form.lineSplittingEnabled !== (location?.line_splitting_enabled ?? false) ||
            form.lineSplittingThreshold !== (location?.line_splitting_threshold ?? '500.00') ||
            JSON.stringify(form.serialNumberMappings.filter((v) => v)) !==
                JSON.stringify(location?.serial_number_mappings || []));

    return (
        <>
            <Page
                title="Managing Retail Location"
                primaryAction={{
                    disabled: !hasChanges,
                    content: 'Validate & Save Changes',
                    onAction: () => handleSaveChanges(form),
                }}
                secondaryActions={[
                    {
                        content: 'Go Back',
                        onAction: () => navigate(`/locations`),
                    },
                ]}
                action
            >
                {!isLoading && (
                    <BlockStack gap="800" title="Location Details">
                        <Card>
                            <BlockStack gap="400">
                                <Text as="h2" variant="headingSm">
                                    Location Details for: {location.shopify_location.name}
                                </Text>
                                <CountryPicker
                                    label="Planet ISO Country"
                                    countries={countries}
                                    value={form.planetMerchantIsoCountry}
                                    onChange={(value) => setFormState({ planetMerchantIsoCountry: value })}
                                    readOnly={form.disableCountrySelector}
                                />
                                <TextField
                                    label="Planet Merchant ID"
                                    value={form.planetMerchantId}
                                    onChange={(value) => setFormState({ planetMerchantId: value })}
                                    autoComplete="off"
                                    readOnly={form.processing}
                                />
                                <TextField
                                    label="Planet Merchant VAT ID"
                                    value={form.planetMerchantVatId}
                                    onChange={(value) => setFormState({ planetMerchantVatId: value })}
                                    autoComplete="off"
                                    readOnly={form.processing}
                                    placeholder="Note: value hidden for security. Leaving this field blank will not change its value."
                                />
                                <TextField
                                    label="Planet POS Username"
                                    value={form.planetUsername}
                                    onChange={(value) => setFormState({ planetUsername: value })}
                                    autoComplete="off"
                                    readOnly={form.processing}
                                    placeholder="Note: value hidden for security. Leaving this field blank will not change its value."
                                />
                                <TextField
                                    label="Planet POS Password"
                                    value={form.planetPassword}
                                    onChange={(value) => setFormState({ planetPassword: value })}
                                    autoComplete="off"
                                    type="password"
                                    readOnly={form.processing}
                                    placeholder="Note: value hidden for security. Leaving this field blank will not change its value."
                                />
                                <Checkbox
                                    label="Is Production?"
                                    checked={form.isProduction}
                                    onChange={(value) => setFormState({ isProduction: value })}
                                />
                                <Checkbox
                                    label="Allow Order Consolidation"
                                    helpText="When enabled, multiple orders can be consolidated into a single tax-free form"
                                    checked={form.allowConsolidation}
                                    onChange={(value) => setFormState({ allowConsolidation: value })}
                                />
                                {form.error && (
                                    <InlineStack gap="100">
                                        <Text tone="critical" fontWeight="bold">
                                            Error:
                                        </Text>
                                        <Text tone="critical">{form.error}</Text>
                                    </InlineStack>
                                )}
                            </BlockStack>
                        </Card>
                        <Checkbox
                            label="Show Advanced Settings"
                            helpText="Check to show advanced fiscal and invoicing settings."
                            checked={form.showAdvancedSettings}
                            onChange={(value) => setFormState({ showAdvancedSettings: value })}
                        />
                        {form.showAdvancedSettings && (
                            <Card title="Advanced Settings">
                                <Box paddingBlockEnd={600}>
                                    <BlockStack gap="200">
                                        <Text variant="headingMd" as="h3">
                                            Fiscal Number Configuration
                                        </Text>
                                        <Select
                                            label="Fiscal Number Type"
                                            options={PlanetFiscalNumberType.toPolarisSelect()}
                                            value={form.planetFiscalNumberType}
                                            onChange={(value) => setFormState({ planetFiscalNumberType: value })}
                                            disabled={form.processing}
                                        />
                                        {form.planetFiscalNumberType === 'custom' && (
                                            <>
                                                <BlockStack gap="200">
                                                    <Text>
                                                        Configure how fiscal number is mapped from Shopify metafields or
                                                        metaobjects. Leave fields empty to use default fallbacks.
                                                    </Text>
                                                    <Text variant="headingMd" as="h3">
                                                        Fiscal Number
                                                    </Text>
                                                    {renderFiscalFieldContent('fiscal_number')}
                                                </BlockStack>
                                                {location?.country?.iso_short_code === 'IT' && (
                                                    <BlockStack gap="200">
                                                        <Text variant="headingMd" as="h3">
                                                            Void Fiscal Number
                                                        </Text>
                                                        {renderFiscalFieldContent('void_fiscal_number')}
                                                    </BlockStack>
                                                )}
                                            </>
                                        )}
                                    </BlockStack>
                                </Box>
                                <Box paddingBlockEnd={600}>
                                    <BlockStack gap="200">
                                        <Text variant="headingMd" as="h3">
                                            Invoice Number Configuration
                                        </Text>
                                        <Select
                                            label="Invoice Number Type"
                                            options={PlanetInvoiceNumberType.toPolarisSelect()}
                                            value={form.planetInvoiceNumberType}
                                            onChange={(value) => setFormState({ planetInvoiceNumberType: value })}
                                            disabled={form.processing}
                                        />
                                        {form.planetInvoiceNumberType === 'custom' && (
                                            <>
                                                <BlockStack gap="200">
                                                    <Text>
                                                        Configure how invoice number is mapped from Shopify metafields
                                                        or metaobjects. Leave fields empty to use default fallbacks.
                                                    </Text>
                                                    <Text variant="headingMd" as="h3">
                                                        Invoice Number
                                                    </Text>
                                                    {renderFiscalFieldContent('invoice_number')}
                                                </BlockStack>
                                            </>
                                        )}
                                    </BlockStack>
                                </Box>
                                {location?.country?.iso_short_code === 'PT' && (
                                    <Box paddingBlockEnd={600}>
                                        <BlockStack gap="200">
                                            <Text variant="headingMd" as="h3">
                                                Hash (Portugal Only)
                                            </Text>
                                            {renderFiscalFieldContent('hash')}
                                        </BlockStack>
                                    </Box>
                                )}
                                <Box paddingBlockEnd={600}>
                                    <BlockStack gap="200">
                                        <Text variant="headingMd" as="h3">
                                            Printer Configuration
                                        </Text>
                                        <BlockStack>
                                            <Checkbox
                                                label="Is PrintNode enabled?"
                                                checked={form.printNodeEnabled}
                                                onChange={(value) => setFormState({ printNodeEnabled: value })}
                                            />
                                            {form.printNodeEnabled && (
                                                <TextField
                                                    label="PrintNode API Key"
                                                    value={form.printNodeApiKey || ''}
                                                    onChange={(value) => setFormState({ printNodeApiKey: value })}
                                                    readOnly={form.processing}
                                                />
                                            )}
                                        </BlockStack>
                                    </BlockStack>
                                </Box>

                                {/* Customs Compliance - Line Splitting (PT, ES, FR) */}
                                {['PT', 'ES', 'FR'].includes(location?.country?.iso_short_code) && (
                                    <Box paddingBlockEnd={600}>
                                        <BlockStack gap="300">
                                            <Text variant="headingMd" as="h3">
                                                Customs Compliance - Line Splitting
                                            </Text>
                                            <Text variant="bodyMd" tone="subdued">
                                                For Portuguese, Spanish, and French customs regulations. Items with
                                                value exceeding the threshold will be split into individual lines
                                                (quantity = 1).
                                            </Text>
                                            <Checkbox
                                                label="Enable line splitting for high-value items"
                                                helpText={
                                                    location?.country?.iso_short_code === 'PT'
                                                        ? 'Portugal: Applies to all product categories'
                                                        : 'Spain/France: Applies to specific product categories only'
                                                }
                                                checked={form.lineSplittingEnabled}
                                                onChange={(value) => setFormState({ lineSplittingEnabled: value })}
                                            />
                                            {form.lineSplittingEnabled && (
                                                <TextField
                                                    label="Value Threshold (€)"
                                                    type="number"
                                                    value={form.lineSplittingThreshold}
                                                    onChange={(value) =>
                                                        setFormState({ lineSplittingThreshold: value })
                                                    }
                                                    helpText="Items with individual value above this threshold will be split"
                                                    readOnly={form.processing}
                                                    min={0}
                                                    step={0.01}
                                                />
                                            )}
                                        </BlockStack>
                                    </Box>
                                )}

                                {/* Serial Number Mapping */}
                                {location?.country?.iso_short_code == 'PT' && (
                                    <Box paddingBlockEnd={600}>
                                        <BlockStack gap="300">
                                            <Text variant="headingMd" as="h3">
                                                Serial Number Configuration
                                            </Text>
                                            <Text variant="bodyMd" tone="subdued">
                                                Configure how serial numbers are generated from product attributes.
                                                Select up to 4 attributes that will be concatenated to create a unique
                                                identifier for customs.
                                            </Text>
                                            <InlineStack gap="300" wrap={false}>
                                                {[0, 1, 2, 3].map((index) => (
                                                    <Select
                                                        key={index}
                                                        label={`Attribute ${index + 1}`}
                                                        options={serialNumberAttributeOptions}
                                                        value={form.serialNumberMappings[index] || ''}
                                                        onChange={(value) => {
                                                            const newMappings = [...form.serialNumberMappings];
                                                            newMappings[index] = value;
                                                            setFormState({ serialNumberMappings: newMappings });
                                                        }}
                                                        disabled={form.processing}
                                                    />
                                                ))}
                                            </InlineStack>
                                            {form.serialNumberMappings.filter((v) => v).length > 0 && (
                                                <Text variant="bodySm" tone="subdued">
                                                    Preview: {form.serialNumberMappings.filter((v) => v).join(' - ')}
                                                </Text>
                                            )}
                                        </BlockStack>
                                    </Box>
                                )}
                                {/* Auto Voiding for return / exchange process */}
                                <Box paddingBlockEnd={600}>
                                    <BlockStack gap="200">
                                        <Text variant="headingMd" as="h3">
                                            Automatic Voiding
                                        </Text>
                                        <Checkbox
                                            label="Auto Voiding for return / exchange process"
                                            helpText="When enabled, completed tax free forms are automatically voided when a return is closed in Shopify."
                                            checked={form.autoVoidingEnabled}
                                            onChange={(value) => setFormState({ autoVoidingEnabled: value })}
                                        />
                                    </BlockStack>
                                </Box>
                            </Card>
                        )}
                    </BlockStack>
                )}
            </Page>
        </>
    );
}

export default LocationDetailsPage;
